/**
 * The integrations catalog.
 *
 * Populated from `GET /integrations`, never a hardcoded list. Three states are
 * kept distinct because the handoff says conflating them produces false
 * counters:
 *
 *   • **Gated / unavailable** — the CDK flag is off, the route was never
 *     deployed (404), or the backend answered 503 `not_configured`. The
 *     product does not exist here; this is not "nothing connected".
 *   • **Available but unconfigured** — the routes exist, individual apps do
 *     not. That is per-card, and the card says so.
 *   • **Connected / not connected** — the only genuinely user-facing state.
 *
 * `activeCount` and `availableCount` are shown as the server's own figures and
 * labelled as such, because neither is necessarily the number of visible cards.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Lock, Plug, RefreshCw } from "lucide-react";
import { Page, PageHeader, EmptyState, ErrorState, SkeletonCards } from "components/ui";
import { Button } from "components/ui/button";
import { toast } from "components/ui/toast";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "components/ui/dialog";
import {
    describeFailure,
    useCatalog,
    useDisconnectApp,
    useStartConnect,
} from "core/features/integrations";
import type { CatalogApp } from "core/features/integrations";
import { AppCard, appCardId } from "./AppCard";
import { ImportProfilePanel } from "./ImportProfilePanel";

/** Which card started a connection, so focus can return to it afterwards. */
const FOCUS_KEY = "merko.integrations.focus";

/** The handoff specifies an HTTPS Composio URL; anything else is not opened. */
function isHttpsUrl(value: unknown): value is string {
    try {
        return typeof value === "string" && new URL(value).protocol === "https:";
    } catch {
        return false;
    }
}

export function IntegrationsCatalog() {
    const catalog = useCatalog();
    const connect = useStartConnect();
    const disconnect = useDisconnectApp();

    const [connectingId, setConnectingId] = useState<string | null>(null);
    const [pendingDisconnect, setPendingDisconnect] = useState<CatalogApp | null>(null);
    const [managing, setManaging] = useState<CatalogApp | null>(null);

    /**
     * Start a connection and hand the user to the provider.
     *
     * The redirect has to happen inside this click: a popup or navigation
     * started later is blocked. `window.location.assign` is used rather than a
     * new tab so the trusted callback page comes back into this same session,
     * where the stored attempt lives.
     */
    const handleConnect = useCallback(
        async (app: CatalogApp) => {
            setConnectingId(app.id);
            try {
                const attempt = await connect.mutateAsync(app.id);
                if (!isHttpsUrl(attempt.redirect_url)) {
                    toast({
                        title: "Could not start the connection",
                        description: "The connection link was not valid. Please try again.",
                        variant: "error",
                    });
                    return;
                }
                try {
                    sessionStorage.setItem(FOCUS_KEY, app.id);
                } catch {
                    /* focus return is a nicety; the connection does not depend on it */
                }
                // Never logged, never stored — opened and forgotten.
                window.location.assign(attempt.redirect_url);
            } catch (error) {
                const failure = describeFailure(error);
                if (failure.code === "already_connected") {
                    // Refresh the card rather than creating a second account.
                    void catalog.refetch();
                    toast({ title: "Already connected", description: failure.message });
                } else {
                    toast({
                        title: "Could not start the connection",
                        description: failure.message,
                        variant: "error",
                    });
                }
            } finally {
                setConnectingId(null);
            }
        },
        [catalog, connect],
    );

    const handleDisconnect = useCallback(async () => {
        const app = pendingDisconnect;
        if (!app) return;
        setPendingDisconnect(null);
        try {
            await disconnect.mutateAsync(app.id);
            toast({
                title: `${app.name} disconnected`,
                description: "Your imported contacts have been kept.",
            });
        } catch (error) {
            toast({
                title: "Could not disconnect",
                description: describeFailure(error).message,
                variant: "error",
            });
        }
    }, [disconnect, pendingDisconnect]);

    const apps = catalog.data?.apps ?? [];

    /* Back from a provider redirect: return focus to the card that started it,
       so a keyboard or screen-reader user lands where they left off. */
    const loaded = apps.length > 0;
    useEffect(() => {
        if (!loaded) return;
        let appId: string | null = null;
        try {
            appId = sessionStorage.getItem(FOCUS_KEY);
            sessionStorage.removeItem(FOCUS_KEY);
        } catch {
            return;
        }
        if (appId) document.getElementById(appCardId(appId))?.focus();
    }, [loaded]);
    const grouped = useMemo(() => {
        const byCategory = new Map<string, CatalogApp[]>();
        for (const app of apps) {
            const list = byCategory.get(app.category) ?? [];
            list.push(app);
            byCategory.set(app.category, list);
        }
        return [...byCategory.entries()].sort(([a], [b]) => a.localeCompare(b));
    }, [apps]);

    // ── gated / unavailable ────────────────────────────────────────────────
    if (catalog.unavailable && !catalog.isLoading) {
        return (
            <Page>
                <PageHeader
                    icon={Plug}
                    title="Integrations"
                    description="Connect the tools your contacts already live in."
                />
                <EmptyState
                    variant="empty"
                    icon={Lock}
                    title="Integrations aren’t enabled here"
                    description={
                        catalog.gated
                            ? "This environment does not have the integrations service switched on yet. An administrator needs to deploy and enable it."
                            : catalog.failure?.message ??
                              "This environment does not have the integrations service switched on yet."
                    }
                    hints={[
                        {
                            icon: AlertTriangle,
                            title: "This is not about your account",
                            description:
                                "Nothing is wrong with your connections — the service itself is not available in this environment.",
                        },
                    ]}
                />
            </Page>
        );
    }

    if (catalog.isLoading) {
        return (
            <Page>
                <PageHeader icon={Plug} title="Integrations" description="Loading the catalogue…" />
                <SkeletonCards count={6} />
            </Page>
        );
    }

    if (catalog.isError) {
        return (
            <Page>
                <PageHeader icon={Plug} title="Integrations" />
                <ErrorState
                    title="Couldn’t load integrations"
                    description={catalog.failure?.message}
                    onRetry={() => void catalog.refetch()}
                />
            </Page>
        );
    }

    return (
        <Page width="full">
            <PageHeader
                icon={Plug}
                title="Integrations"
                description="Connect the tools your contacts already live in, then set up an import."
                actions={
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => void catalog.refetch()}
                        loading={catalog.isFetching}
                    >
                        <RefreshCw className="h-4 w-4" aria-hidden="true" />
                        Refresh
                    </Button>
                }
            />

            {/* The server's own figures, labelled as such: they count on
                different bases and neither matches the visible card count. */}
            <p className="mb-5 text-meta text-content-muted">
                {catalog.data?.activeCount ?? 0} connected · {catalog.data?.availableCount ?? 0}{" "}
                available to connect · {apps.length} in the catalogue
            </p>

            {apps.length === 0 ? (
                <EmptyState
                    variant="empty"
                    icon={Plug}
                    title="No apps in the catalogue"
                    description="Nothing is configured for this environment yet."
                />
            ) : (
                <div className="space-y-7">
                    {grouped.map(([category, list]) => (
                        <section key={category}>
                            <h2 className="mb-3 text-ui font-semibold text-content">{category}</h2>
                            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                                {list.map((app) => (
                                    <AppCard
                                        key={app.id}
                                        app={app}
                                        connecting={connectingId === app.id}
                                        disconnecting={
                                            disconnect.isPending && disconnect.variables === app.id
                                        }
                                        onConnect={(next) => void handleConnect(next)}
                                        onDisconnect={setPendingDisconnect}
                                        onManage={setManaging}
                                    />
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
            )}

            {/* Disconnection is deliberate, and what it keeps is worth stating:
                contacts, suppressions, job history and native connections all
                survive it. */}
            <Dialog
                open={!!pendingDisconnect}
                onOpenChange={(open) => !open && setPendingDisconnect(null)}
            >
                <DialogContent size="sm">
                    <DialogHeader>
                        <DialogTitle>Disconnect {pendingDisconnect?.name}?</DialogTitle>
                        <DialogDescription>
                            This app and its import profiles stop working straight away. Your
                            imported contacts, suppressions and past import history are kept, and a
                            native connection — if you have one — is not affected.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setPendingDisconnect(null)}>
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            loading={disconnect.isPending}
                            onClick={() => void handleDisconnect()}
                        >
                            Disconnect
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {managing && (
                <ImportProfilePanel app={managing} onClose={() => setManaging(null)} />
            )}
        </Page>
    );
}

export default IntegrationsCatalog;
